import { createApp, defineAsyncComponent } from 'vue';

import './styles.css';
import 'mosha-vue-toastify/dist/style.css';
// Font Awesome optimisé - CSS de base uniquement
import './styles/fontawesome-optimized.css';

import VueCookies from 'vue-cookies';
import VueMatomo from 'vue-matomo';
import router from './router.js';
import store from './store';
import api from './plugins/api.js';
import analyzerApi from './plugins/analyzerApi.js';
import i18n, { syncDocumentLang } from './plugins/i18n.js';
import utils from './plugins/utils.js';
import crisp from './plugins/crisp.js';
import clickOutside from './plugins/clickOutside';
import updateImgSrc from './plugins/updateImgSrc';
import { cleanupLegacyLocalStorage } from './utils/cleanupLocalStorage.js';
import {
  initMixpanel,
  getMixpanel,
  syncMixpanelUser,
  captureAttributionFromCurrentUrl,
  getStoredAttributionPayload,
  trackMixpanelEvent,
} from './plugins/mixpanel.js';
import {
  initLinkedInInsight,
  trackLinkedInInsightConversion,
  trackLinkedInInsightPageView,
} from './plugins/linkedinInsight.js';
import {
  trackMetaPixel,
  trackMetaPixelPageView,
} from './plugins/metaPixel.js';
import {
  trackOpenAiAdsPixel,
  trackOpenAiAdsPixelPageView,
} from './plugins/openAiAdsPixel.js';
import { isMarketingPublicPath, normalizeMarketingPath } from './utils/marketingPages.js';
import { ensureCanonicalLink } from './composables/useMarketingPageMeta.js';
import { getLandingCanonicalUrl } from './utils/landingJsonLd.js';
import {
  installFrontendErrorReporting,
  reportFrontendError,
} from './plugins/errorReporter.js';

import App from './App.vue';
import Icon from './components/Icon.vue';

(function applyNonProdRobotsMeta() {
  try {
    const host = String(window.location.hostname || '').toLowerCase();
    const prodHosts = new Set(['studio.mydid.com', 'www.studio.mydid.com']);
    const force =
      process.env.VUE_APP_NOINDEX === '1' ||
      process.env.VUE_APP_ENV === 'preprod' ||
      !!(process.env.VUE_APP_PASSWORD && String(process.env.VUE_APP_PASSWORD).length);
    if (!force && prodHosts.has(host)) return;
    if (!document.querySelector('meta[name="robots"]')) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }
  } catch (_err) {
    /* ignore */
  }
})();

const AsyncAppLoader = defineAsyncComponent(() =>
  import(/* webpackChunkName: "ui-loader" */ './components/AppLoader.vue')
);
const AsyncVue3Lottie = defineAsyncComponent(() =>
  import(/* webpackChunkName: "ui-lottie" */ 'vue3-lottie').then(
    (module) => module.Vue3Lottie
  )
);

const MATERIAL_ICON_STYLESHEETS = {
  outlined: [
    '/fonts/material-icons/material-icons-outlined.css',
    'deferred-material-icons-outlined',
    'Material Icons Outlined',
  ],
  round: [
    '/fonts/material-icons/material-icons-round.css',
    'deferred-material-icons-round',
    'Material Icons Round',
  ],
  symbols: [
    '/fonts/material-icons/material-symbols-outlined.css',
    'deferred-material-symbols-outlined',
    'Material Symbols Outlined',
  ],
};

const MATERIAL_ICON_READY_TIMEOUT_MS = 3000;

function materialIconSheetsForRoute(path = '') {
  const routePath = String(path || '/');
  if (routePath === '/signin' || isMarketingPublicPath(routePath)) return [];
  return [
    MATERIAL_ICON_STYLESHEETS.outlined,
    MATERIAL_ICON_STYLESHEETS.round,
    MATERIAL_ICON_STYLESHEETS.symbols,
  ];
}
const DM_SANS_STYLESHEET =
  'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap';
const loadedDeferredStylesheets = new Set();

function deferNonCriticalWork(task) {
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: 1800 });
    return;
  }
  window.setTimeout(task, 0);
}

function appendPreconnectOnce(id, href, crossOrigin = false) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'preconnect';
  link.href = href;
  if (crossOrigin) link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

function appendStylesheetOnce(id, href, onComplete = null) {
  if (typeof document === 'undefined') return;
  if (loadedDeferredStylesheets.has(id) || document.getElementById(id)) {
    if (typeof onComplete === 'function') window.setTimeout(onComplete, 0);
    return;
  }
  loadedDeferredStylesheets.add(id);
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  if (typeof onComplete === 'function') {
    link.onload = onComplete;
    link.onerror = onComplete;
  }
  document.head.appendChild(link);
}

function markMaterialIconsReady() {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.add('material-icons-ready');
}

function waitForMaterialIconFonts(families) {
  if (typeof document === 'undefined') return;
  if (document.documentElement.classList.contains('material-icons-ready')) return;

  const supportsFontLoadingApi =
    typeof document.fonts !== 'undefined' &&
    typeof document.fonts.load === 'function';

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    if (timeoutId) window.clearTimeout(timeoutId);
    markMaterialIconsReady();
  };

  // Safety net: never leave icons stuck invisible if the font load rejects,
  // times out or the CSS Font Loading API is unavailable.
  const timeoutId = window.setTimeout(finish, MATERIAL_ICON_READY_TIMEOUT_MS);

  if (!supportsFontLoadingApi) {
    // No CSS Font Loading API: the <link>.onload fallback (handled by the
    // caller) will mark ready; the timeout above guarantees it regardless.
    return;
  }

  // 'visibility' is a valid ligature in all three icon fonts, so it also
  // forces the actual glyph to be downloaded rather than just the metadata.
  const loaders = families.map((family) =>
    document.fonts.load(`24px "${family}"`, 'visibility')
  );

  Promise.allSettled(loaders)
    .then(() => document.fonts.ready)
    .then(finish)
    .catch(finish);
}

function loadMaterialIconStylesheets(routePath = '') {
  if (typeof document === 'undefined') return;

  const sheets = materialIconSheetsForRoute(routePath);
  if (!sheets.length) return;
  if (document.documentElement.classList.contains('material-icons-ready')) return;

  const supportsFontLoadingApi =
    typeof document.fonts !== 'undefined' &&
    typeof document.fonts.load === 'function';

  // Fallback path for browsers without the CSS Font Loading API: keep the
  // legacy <link>.onload behaviour so the ready class is still set.
  let pendingCount = sheets.length;
  const onSheetLoaded = () => {
    if (supportsFontLoadingApi) return;
    pendingCount -= 1;
    if (pendingCount <= 0) markMaterialIconsReady();
  };

  sheets.forEach(([href, id]) => appendStylesheetOnce(id, href, onSheetLoaded));

  // Wait on the real fonts (woff2/ttf) being downloaded, not just the CSS
  // being parsed, so the ligature text never flashes.
  const families = sheets.map((sheet) => sheet[2]).filter(Boolean);
  waitForMaterialIconFonts(families);
}

function loadDmSansStylesheet() {
  appendPreconnectOnce('deferred-google-fonts-preconnect', 'https://fonts.googleapis.com');
  appendPreconnectOnce('deferred-google-fonts-gstatic-preconnect', 'https://fonts.gstatic.com', true);
  appendStylesheetOnce('deferred-dm-sans', DM_SANS_STYLESHEET);
}

function syncRouteDeferredStyles(path = '') {
  const routePath = String(path || window.location?.pathname || '/');
  if (routePath !== '/signin' && !isMarketingPublicPath(routePath)) {
    loadMaterialIconStylesheets(routePath);
  }
  if (routePath === '/brand-analyzer') {
    deferNonCriticalWork(loadDmSansStylesheet);
  }
}

// Nettoyer les anciennes clés localStorage au démarrage (une seule fois)
const cleanupVersion = '1.0.0';
const cleanupKey = 'analyzer_cleanup_version';
if (localStorage.getItem(cleanupKey) !== cleanupVersion) {
  cleanupLegacyLocalStorage();
  localStorage.setItem(cleanupKey, cleanupVersion);
}

const app = createApp(App);

app.use(store);
app.use(router);
app.use(api);
app.use(utils);
app.use(i18n);
// Langue du document = locale i18n (évite que le navigateur traduise la page)
syncDocumentLang(i18n.global.locale.value);
app.use(analyzerApi);
installFrontendErrorReporting(app, { router, store, i18n });
app.use(VueMatomo, {
  host: process.env.VUE_APP_MATOMO_URL,
  siteId: process.env.VUE_APP_MATOMO_SITE_ID,
  router,
  trackerUrl: process.env.VUE_APP_MATOMO_TRACKER_URL,
  trackerScriptUrl: process.env.VUE_APP_MATOMO_TRACKER_SCRIPT_URL,
  requireConsent: true,
});

initMixpanel();
function resolveLandingCanonicalFromBrowser() {
  try {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang === 'fr' || urlLang === 'en') return getLandingCanonicalUrl(urlLang);
  } catch (_err) {
    /* ignore */
  }
  try {
    const saved = localStorage.getItem('user-locale') || localStorage.getItem('locale');
    if (saved === 'fr' || saved === 'en') return getLandingCanonicalUrl(saved);
  } catch (_err) {
    /* ignore */
  }
  return getLandingCanonicalUrl('en');
}

if (normalizeMarketingPath(window.location.pathname) === '/') {
  ensureCanonicalLink(resolveLandingCanonicalFromBrowser());
}
if (isMarketingPublicPath(window.location.pathname)) {
  deferNonCriticalWork(() => initLinkedInInsight());
} else {
  initLinkedInInsight();
}
captureAttributionFromCurrentUrl();
app.provide('getMixpanel', getMixpanel);
app.provide('mixpanelTrack', (eventName, props, options = {}) => {
  const session = store.getters.getSessionInfo;
  const issuerInfo = store.getters.getCurrentIssuerInfo;
  syncMixpanelUser({ session, issuerInfo });
  return trackMixpanelEvent(eventName, props || {}, {
    session,
    issuerInfo,
    requireDistinctId: options.requireDistinctId !== false,
    attachContext: options.attachContext !== false,
  });
});
app.provide('linkedinTrackConversion', (conversionId, props) =>
  trackLinkedInInsightConversion(conversionId, props || {})
);
app.provide('metaPixelTrack', (eventName, props) =>
  trackMetaPixel(eventName, props || {})
);
app.provide('openAiAdsPixelTrack', (eventName, props) =>
  trackOpenAiAdsPixel(eventName, props || {})
);

app.config.devtools = true;

// adminToken et adminProfile restent en JS-readable (décision architecturale)
// token et token_tmp sont maintenant en HttpOnly cookies posés par le serveur
const isSecure = typeof window !== 'undefined' && window.location?.protocol === 'https:';
function setJsCookie(key, value, expires = '30d') {
  VueCookies.set(key, value, expires, '/', '', isSecure, 'Lax');
}
app.provide('cookies', VueCookies);
app.provide('setJsCookie', setJsCookie);
app.component('icon', Icon);
app.component('Vue3Lottie', AsyncVue3Lottie);
app.component('AppLoader', AsyncAppLoader);

app.directive('click-outside', clickOutside);
app.directive('update-img-src', updateImgSrc);

app.mixin({
  mounted() {
    updateImgSrc.mounted();
  },
});

function syncCrispState(routePath = null) {
  crisp.sync({
    session: store.getters.getSessionInfo,
    issuerInfo: store.getters.getCurrentIssuerInfo,
    path:
      routePath ||
      router.currentRoute.value?.path ||
      window.location.pathname,
  });
}

function syncMixpanelUserState() {
  const session = store.getters.getSessionInfo;
  const issuerInfo = store.getters.getCurrentIssuerInfo;
  syncMixpanelUser({ session, issuerInfo, force: true });
}

const LEAD_FREE_TEST_TRACKING_KEY = 'leadFreeTestTrackingContextV1';

function trackFreeTestDashboardConversion(to, session, issuerInfo) {
  if (!String(to?.path || '').startsWith('/brand-analyzer')) return;
  if (!session || session.isTmp || !String(session.studioAccountId || '').trim()) return;
  if (typeof localStorage === 'undefined') return;

  try {
    const raw = localStorage.getItem(LEAD_FREE_TEST_TRACKING_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== 'object' || !stored.free_test_attempt_id) return;

    const commonProps = {
      free_test_attempt_id: stored.free_test_attempt_id,
      analysis_id: stored.analysis_id || null,
      signup_source: stored.signup_source || 'landing_free_test',
      free_test_score_band: stored.score_band || null,
      free_test_has_negative_signal: stored.has_negative_signal === true,
      free_test_confidence_level: stored.confidence_level || null,
      locale: stored.locale || null,
      environment: stored.environment || (process.env.NODE_ENV === 'production' ? 'production' : 'development'),
      test_mode: stored.test_mode === true || (stored.environment || process.env.NODE_ENV) !== 'production',
      is_internal: stored.is_internal === true,
      ...getStoredAttributionPayload(),
    };

    if (stored.signup_completed_tracked !== true) {
      trackMixpanelEvent('Signup Completed', {
        signup_context: stored.signup_context || 'dashboard_recovery',
        signup_method: stored.signup_method || 'unknown',
        ...commonProps,
        $insert_id: `free-test-signup-completed:${stored.free_test_attempt_id}`,
      }, {
        session,
        issuerInfo,
        requireDistinctId: true,
        attachContext: true,
      });
      // Recovery path: vrai compte déjà créé — CompleteRegistration (pas le magic link).
      try {
        const metaKey = `meta_complete_registration:${stored.free_test_attempt_id}`;
        if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(metaKey)) {
          sessionStorage.setItem(metaKey, '1');
          trackMetaPixel('CompleteRegistration', {
            content_name: 'studio_signup',
            signup_context: stored.signup_context || 'dashboard_recovery',
            signup_method: stored.signup_method || 'unknown',
            signup_source: stored.signup_source || 'landing_free_test',
          });
          trackOpenAiAdsPixel('CompleteRegistration', {
            content_name: 'studio_signup',
            signup_context: stored.signup_context || 'dashboard_recovery',
            signup_method: stored.signup_method || 'unknown',
            signup_source: stored.signup_source || 'landing_free_test',
            event_id: `signup-completed:${stored.free_test_attempt_id}`,
          });
        }
      } catch (_err) {
        trackMetaPixel('CompleteRegistration', {
          content_name: 'studio_signup',
          signup_context: stored.signup_context || 'dashboard_recovery',
        });
        trackOpenAiAdsPixel('CompleteRegistration', {
          content_name: 'studio_signup',
          signup_context: stored.signup_context || 'dashboard_recovery',
          event_id: stored.free_test_attempt_id
            ? `signup-completed:${stored.free_test_attempt_id}`
            : undefined,
        });
      }
    }

    const dashboardTracked = trackMixpanelEvent('Free Test Dashboard Viewed', {
      ...commonProps,
      dashboard_path: to.fullPath || to.path,
      $insert_id: `free-test-dashboard-viewed:${stored.free_test_attempt_id}`,
    }, {
      session,
      issuerInfo,
      requireDistinctId: true,
      attachContext: true,
    });

    if (dashboardTracked) localStorage.removeItem(LEAD_FREE_TEST_TRACKING_KEY);
  } catch (_) {
    // Conversion analytics must never affect dashboard navigation.
  }
}

syncCrispState();
syncMixpanelUserState();
syncRouteDeferredStyles(window.location.pathname);

store.watch(
  (state) => state.sessionInfo,
  () => {
    syncCrispState();
    syncMixpanelUserState();
    trackFreeTestDashboardConversion(
      router.currentRoute.value,
      store.getters.getSessionInfo,
      store.getters.getCurrentIssuerInfo
    );
  }
);

store.watch(
  (state) => state.currentIssuerInfo,
  () => {
    syncCrispState();
    syncMixpanelUserState();
  }
);

router.afterEach((to) => {
  if (normalizeMarketingPath(to.path) === '/') {
    ensureCanonicalLink(resolveLandingCanonicalFromBrowser());
  }
  syncCrispState(to.path);
  syncRouteDeferredStyles(to.path);
  captureAttributionFromCurrentUrl();

  // SPA page views for Google Analytics (G-8TZ9SC6M08) + GTM dataLayer
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'virtualPageview',
      pagePath: to.fullPath,
      pageTitle: typeof document !== 'undefined' ? document.title : '',
      pageLocation:
        typeof window.location !== 'undefined' ? window.location.href : '',
    });
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-8TZ9SC6M08', {
        page_path: to.fullPath,
        page_title: typeof document !== 'undefined' ? document.title : '',
        page_location:
          typeof window.location !== 'undefined' ? window.location.href : '',
      });
    }
  }

  if (isMarketingPublicPath(to.path)) {
    deferNonCriticalWork(() => {
      trackLinkedInInsightPageView(to.fullPath);
      trackMetaPixelPageView(to.fullPath);
      trackOpenAiAdsPixelPageView(to.fullPath);
    });
  } else {
    trackLinkedInInsightPageView(to.fullPath);
    trackMetaPixelPageView(to.fullPath);
    trackOpenAiAdsPixelPageView(to.fullPath);
  }
  const session = store.getters.getSessionInfo;
  const issuerInfo = store.getters.getCurrentIssuerInfo;
  trackMixpanelEvent(
    'Page View',
    {
      path: to.fullPath,
      name: to.name != null ? String(to.name) : '',
      current_url:
        typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : to.fullPath,
      ...getStoredAttributionPayload(),
    },
    {
      session,
      issuerInfo,
      requireDistinctId: true,
      attachContext: true,
    }
  );
  syncMixpanelUser({ session, issuerInfo, force: true });
  trackFreeTestDashboardConversion(to, session, issuerInfo);
});

router.isReady().then(() => {
  syncRouteDeferredStyles(router.currentRoute.value?.path);
  app.mount('#app');

  try {
    const loader = document.getElementById('app-loader');
    if (loader) loader.style.display = 'none';
    if (window.__MYDID_BOOT_LOADER_GUARD__?.stop) {
      window.__MYDID_BOOT_LOADER_GUARD__.stop();
    }
  } catch (_err) {}
});

const ASSET_RECOVERY_PREFIX = 'asset_recovery_once_v1';
const ASSET_RECOVERY_COOLDOWN_MS = 2 * 60 * 1000;

function getRecoveryKey() {
  try {
    const path = window?.location?.pathname || '/';
    return `${ASSET_RECOVERY_PREFIX}:${path}`;
  } catch (_err) {
    return ASSET_RECOVERY_PREFIX;
  }
}

function isRecoverableChunkErrorMessage(message = '') {
  const m = String(message || '');
  return (
    m.includes('Loading chunk') ||
    m.includes('ChunkLoadError') ||
    m.includes('Failed to fetch dynamically imported module') ||
    m.includes('Importing a module script failed')
  );
}

function isRecoverableAssetUrl(url = '') {
  const u = String(url || '');
  if (!u) return false;
  if (!/\.(js|css)(\?|#|$)/i.test(u)) return false;
  return (
    u.includes('/js/') ||
    u.includes('/css/') ||
    u.includes('chunk') ||
    u.includes('app.')
  );
}

function tryRecoverMissingAsset(context, url = '') {
  try {
    const now = Date.now();
    const key = getRecoveryKey();
    const lastTs = Number(sessionStorage.getItem(key) || '0');
    if (Number.isFinite(lastTs) && lastTs > 0 && now - lastTs < ASSET_RECOVERY_COOLDOWN_MS) {
      console.warn('[BOOT][asset.recovery] cooldown active', {
        context,
        url,
        cooldownMs: ASSET_RECOVERY_COOLDOWN_MS,
      });
      return false;
    }
    sessionStorage.setItem(key, String(now));

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('_asset_recover', String(now));
    console.warn('[BOOT][asset.recovery] reloading page', { context, url });
    window.location.replace(nextUrl.toString());
    return true;
  } catch (err) {
    console.warn('[BOOT][asset.recovery] failed', err?.message || err);
    return false;
  }
}

// Gestion globale des erreurs de promesses non gérées
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const message = String(error?.message || error || '');
  
  // Ignorer les erreurs Axios qui sont déjà gérées par l'intercepteur
  if (error && (
    error.isAxiosError || 
    (message && message.includes('Network Error')) ||
    (error.name === 'AxiosError')
  )) {
    // Les erreurs sont déjà gérées par l'intercepteur axios qui affiche un toast
    // On empêche juste qu'elles remontent comme des erreurs non gérées
    event.preventDefault();
    return;
  }

  // Recovery auto pour les erreurs de chunks/assets après déploiement (404 intermittent).
  if (isRecoverableChunkErrorMessage(message)) {
    event.preventDefault();
    tryRecoverMissingAsset('unhandledrejection', message);
    return;
  }

  reportFrontendError(error, {
    router,
    store,
    locale: i18n.global.locale.value,
    context: { source: 'unhandledrejection' },
  });
  
  // Pour les autres erreurs non-Axios, on les laisse passer pour le débogage
});

window.addEventListener('error', (event) => {
  // Resource loading error (script/link/img) - capter l'URL exacte.
  const target = event?.target;
  if (target && target !== window) {
    const resourceUrl = target.src || target.href || '';
    if (isRecoverableAssetUrl(resourceUrl)) {
      if (tryRecoverMissingAsset('resource-error', resourceUrl)) {
        event.preventDefault?.();
        return;
      }
    }
    console.warn('[BOOT][resource.error]', {
      tagName: target.tagName || null,
      url: resourceUrl || null,
    });
    return;
  }

  console.error('[BOOT][window.error]', {
    message: event?.message || 'unknown_error',
    filename: event?.filename || null,
    lineno: event?.lineno || null,
    colno: event?.colno || null,
  });
  reportFrontendError(event?.error || event?.message, {
    router,
    store,
    locale: i18n.global.locale.value,
    message: event?.message || 'unknown_error',
    filename: event?.filename || null,
    lineno: event?.lineno || null,
    colno: event?.colno || null,
    context: { source: 'window.error' },
  });
}, true);

const currentPath = window.location.pathname;
const isMarketingPage = isMarketingPublicPath(currentPath);
if (!isMarketingPage) {
  import(/* webpackChunkName: "floating-vue" */ 'floating-vue').then(({ default: FloatingVue }) => {
    app.use(FloatingVue);
  });
}
